/**
 * GraphQL Queries for Metadata V1 API
 */

/**
 * Query to fetch metadata types (folders)
 */
export const GET_METADATA_TYPES_QUERY = `
  query GetMetadataTypes($orgId: ID!, $retrievalId: ID!) {
    metadataTypes(organizationId: $orgId, retrievalId: $retrievalId) {
      metadataType
      count
      __typename
    }
  }
`;

/**
 * Query to fetch retrieval metadata items (files)
 */
export const GET_RETRIEVAL_ITEMS_QUERY = `
  query RetrievalMetadataItems(
    $retrievalId: ID!
    $metadataType: String!
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    retrievalMetadataItems(
      retrievalId: $retrievalId
      metadataType: $metadataType
      first: $first
      after: $after
      last: $last
      before: $before
    ) {
      isBundleType
      totalCount
      edges {
        cursor
        node {
          folderName
          itemCount
          items {
            id
            fullName
            fileName
            content
            isEdited
            metadataType
            lastModifiedDate
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

/**
 * Query to fetch a single metadata item with content
 */
export const GET_METADATA_ITEM_QUERY = `
  query GetMetadataItem($organizationId: ID!, $id: ID!) {
    metadataItem(organizationId: $organizationId, id: $id) {
      id
      metadataType
      content
      fullName
      fileName
      isEdited
      status
      __typename
    }
  }
`;

/**
 * Mutation to update a metadata item's content
 */
export const UPDATE_METADATA_ITEM_MUTATION = `
  mutation UpdateMetadataItem($input: UpdateMetadataItemInput!) {
    updateMetadataItem(input: $input) {
      id
      content
      sha256Hash
      metadataType
      isEdited
      fullName
      fileName
      status
      __typename
    }
  }
`;

/**
 * Query to search objects (returns fullName and metadataType only)
 */
export const SEARCH_OBJECTS_QUERY = `
  query SearchObjects($organizationId: ID!, $retrievalId: ID!, $searchString: String!) {
    searchMetadataItems(organizationId: $organizationId, retrievalId: $retrievalId, searchString: $searchString) {
      fullName
      metadataType
    }
  }
`;

// ============================================================================
// BRANCHING QUERIES & MUTATIONS
// ============================================================================

/**
 * Query to list current user's branches for an organization (Relay connection, paginated)
 */
export const USER_BRANCHES_QUERY = `
  query UserBranches($organizationId: ID!, $status: BranchStatus) {
    userBranches(organizationId: $organizationId, status: $status, first: 100) {
      edges {
        node {
          id
          name
          status
          content
          sha256Hash
          baseHash
          hasConflicts
          mergedAt
          insertedAt
          updatedAt
          fullName
          fileName
          metadataType
          metadataItem {
            id
            fullName
            fileName
            metadataType
            content
            sha256Hash
          }
          user {
            id
            email
          }
          retrieval {
            id
          }
        }
      }
    }
  }
`;

/**
 * Query to get the current user's active branch for a specific file
 */
export const ACTIVE_BRANCH_QUERY = `
  query ActiveBranch($metadataItemId: ID!) {
    activeBranch(metadataItemId: $metadataItemId) {
      id
      name
      status
      content
      sha256Hash
      hasConflicts
      baseHash
      metadataItem {
        id
        fullName
        fileName
        metadataType
      }
      user {
        id
        email
      }
      retrieval {
        id
      }
      insertedAt
      updatedAt
    }
  }
`;

/**
 * Mutation to save file content to a branch (creates or updates)
 */
export const SAVE_BRANCH_FILE_MUTATION = `
  mutation SaveBranchFile($input: SaveBranchFileInput!) {
    saveBranchFile(input: $input) {
      id
      name
      status
      content
      sha256Hash
      hasConflicts
      baseHash
      metadataItem {
        id
        fullName
        fileName
        metadataType
      }
      user {
        id
        email
      }
      retrieval {
        id
      }
      insertedAt
      updatedAt
    }
  }
`;

/**
 * Mutation to merge a branch into main
 */
export const MERGE_BRANCH_MUTATION = `
  mutation MergeBranch($input: MergeBranchInput!) {
    mergeBranch(input: $input) {
      success
      mergedBranch {
        id
        status
        mergedAt
      }
      conflict {
        metadataItemId
        fileName
        fullName
        metadataType
        baseHash
        mainHash
        branchHash
        mainContent
        branchContent
      }
    }
  }
`;

/**
 * Mutation to deploy multiple branch files to Salesforce
 * Returns either a deployment (success) or the first conflict found
 */
export const DEPLOY_BRANCH_FILES_MUTATION = `
  mutation DeployBranchFiles($input: DeployBranchFilesInput!) {
    deployBranchFiles(input: $input) {
      deployment {
        id
        status
        asyncProcessId
      }
      conflict {
        metadataItemId
        fileName
        fullName
        metadataType
        baseHash
        mainHash
        branchHash
        mainContent
        branchContent
      }
    }
  }
`;

/**
 * Mutation to abandon (discard) a branch
 */
export const ABANDON_BRANCH_MUTATION = `
  mutation AbandonBranch($input: AbandonBranchInput!) {
    abandonBranch(input: $input) {
      id
      status
    }
  }
`;

/**
 * Mutation to delete a file in the working set (soft-delete branch, status=DELETED)
 */
export const DELETE_BRANCH_FILE_MUTATION = `
  mutation DeleteBranchFile($input: DeleteBranchFileInput!) {
    deleteBranchFile(input: $input) {
      id
      name
      status
      metadataItem {
        id
        fullName
        fileName
        metadataType
      }
    }
  }
`;

// ============================================================================

/**
 * Query to search metadata items by name
 */
export const SEARCH_METADATA_ITEMS_QUERY = `
  query SearchMetadataItems(
    $organizationId: ID!
    $retrievalId: ID!
    $searchString: String!
  ) {
    searchMetadataItems(
      organizationId: $organizationId
      retrievalId: $retrievalId
      searchString: $searchString
    ) {
      id
      metadataType
      fullName
      fileName
      toolingEnriched
      __typename
    }
  }
`;